import { motion } from "motion/react"
import { useRef } from "react"
import { BubbleTail } from "@/components/ui/bubble-tail"
import { CornerTicks } from "@/components/ui/corner-ticks"
import { EncryptedText } from "@/components/ui/encrypted-text"
import { Fact } from "@/components/ui/fact"
import { MutuPrompt } from "@/components/ui/mutu-prompt"
import { SectionTitle } from "@/components/ui/section-title"
import { SharedButton } from "@/components/ui/shared-button"
import { SubjectCaption } from "@/components/ui/subject-caption"
import { TransmissionPanel } from "@/components/ui/transmission-panel"
import { usePinnedReveal } from "@/hooks/use-pinned-reveal"

const QUIP_TEXT =
  "He wrote every line of mine. Including the ones I do not like."

export function CreatorSection() {
  const sectionRef = useRef<HTMLElement>(null)
  const bubbleRef = useRef<HTMLDivElement>(null)
  const bodyRef = useRef<HTMLDivElement>(null)
  const tailRef = useRef<HTMLSpanElement>(null)

  usePinnedReveal({ sectionRef, bubbleRef, bodyRef, tailRef })

  return (
    <section
      ref={sectionRef}
      id="creator"
      className="relative z-10 flex min-h-svh w-full flex-col items-start px-5 py-20 sm:px-10 sm:py-28 md:z-auto lg:py-32"
    >
      <motion.div
        initial="hidden"
        whileInView="shown"
        viewport={{ once: true, amount: 0.25 }}
        className="relative z-30 grid w-full max-w-4xl grid-cols-1 items-center gap-10 sm:ml-auto sm:gap-12 md:grid-cols-2"
      >
        <div className="flex flex-col md:order-2">
          <div className="mx-auto flex w-full max-w-64 justify-center sm:max-w-88">
            <SectionTitle prefix="The" accent="Auteur" />
          </div>
          <div className="mt-8 sm:mt-10">
            <ProfileFrame />
          </div>
        </div>
        <CreatorBody />
      </motion.div>

      <div className="relative z-30 mt-12 w-full max-w-xl sm:absolute sm:top-[18%] sm:left-6 sm:mt-0 sm:max-w-72 md:left-10 md:max-w-80 lg:left-16">
        <div ref={bubbleRef} className="relative">
          <TransmissionPanel>
            <div ref={bodyRef}>
              <MutuPrompt verb="re" subject="Pradeep Shahi" />
              <p className="mt-4 font-mono text-foreground text-sm leading-relaxed sm:text-base sm:leading-[1.7]">
                <EncryptedText
                  text={QUIP_TEXT}
                  encryptedClassName="text-label-dim"
                  revealedClassName="text-foreground"
                  revealDelayMs={45}
                  flipDelayMs={45}
                />
              </p>
            </div>
          </TransmissionPanel>
          <BubbleTail ref={tailRef} side="bottom" />
        </div>
      </div>
    </section>
  )
}

function ProfileFrame() {
  return (
    <motion.div
      variants={{
        hidden: { opacity: 0, y: 14 },
        shown: { opacity: 1, y: 0 },
      }}
      transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
      className="relative mx-auto w-full max-w-64 sm:max-w-88 md:order-2"
    >
      <div className="relative aspect-[3/4] overflow-hidden border border-white/25 bg-background shadow-[0_0_60px_rgb(var(--brand-rgb)/0.06)]">
        <img
          src="https://70fy8xbp6b.ufs.sh/f/BzM3qquY5hLZ8TgMoPArVQWod7GUKpjsamlI6ki9wLHCD2h3"
          alt="Pradeep Shahi"
          width={900}
          height={1200}
          className="h-full w-full object-cover"
          loading="lazy"
          decoding="async"
        />
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 bg-linear-to-t from-black/55 via-transparent to-black/15"
        />
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 mix-blend-overlay"
          style={{
            backgroundImage:
              "repeating-linear-gradient(180deg, rgba(255,255,255,0.05) 0 1px, transparent 1px 4px)",
          }}
        />
        <span className="absolute right-3 bottom-3 font-mono text-[9px] text-label uppercase tracking-[0.3em]">
          Subj. PS-01
        </span>
        <CornerTicks />
      </div>
    </motion.div>
  )
}

function CreatorBody() {
  return (
    <motion.div
      variants={{
        hidden: { opacity: 0, y: 14 },
        shown: { opacity: 1, y: 0 },
      }}
      transition={{ duration: 0.9, delay: 0.2, ease: [0.16, 1, 0.3, 1] }}
      className="text-center md:order-1 md:text-left"
    >
      <SubjectCaption
        name="Pradeep Shahi"
        className="items-center text-center md:items-start md:text-left"
      />
      <p className="mt-6 font-mono text-foreground text-sm leading-relaxed sm:text-base sm:leading-[1.75]">
        Nearly a decade spent capturing the raw stories and ancient echoes of
        his homeland.
      </p>
      <p className="mt-4 font-mono text-foreground text-sm leading-relaxed sm:text-base sm:leading-[1.75]">
        Now forging the EKLO-Verse, a mythic wasteland where ancestral prophecy
        meets a high-altitude zombie apocalypse.
      </p>
      <p className="mt-4 font-mono text-foreground text-sm leading-relaxed sm:text-base sm:leading-[1.75]">
        His most ambitious build yet: a decaying frontier where sci-fi tech and
        old gods collide in a struggle for hope.
      </p>
      <div className="mt-8 grid grid-cols-3 gap-4 border-white/10 border-t pt-5">
        <Fact label="In Cinema" value="9 years" />
        <Fact label="Builds" value="Worlds, first" />
        <Fact label="From" value="Nepal" />
      </div>

      <SharedButton
        to="/auteur"
        className="mx-auto mt-8 inline-flex w-full max-w-xs justify-center md:mx-0 md:w-fit md:max-w-none"
      >
        See Details
      </SharedButton>
    </motion.div>
  )
}
